import { useMemo } from "react";
import { useIssueSources } from "#app/hooks/settings/use-issue-source-mutations";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";

const POOL_ID = "issue-sources";

/**
 * Fetches workspace lead sources and returns an EntityPoolConfig.
 * Reuses the existing useIssueSources() hook from settings.
 */
export function useIssueSourcePool() {
  const { issueSources, isLoading } = useIssueSources();

  const items = useMemo<EntityItemConfig[]>(
    () =>
      issueSources
        .filter((s) => !s.archived)
        .map((s) => ({
          id: s.issueSourceId,
          title: s.name,
          description: s.isDefault ? "Default" : undefined,
          // Sources have no glyph — render an empty visual so the row is
          // label-only and the default initials avatar never shows.
          avatar: { render: () => null },
        })),
    [issueSources],
  );

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Sources",
      items,
      // Carried on the pool so the selector's empty state can tell an
      // unfetched pool from a workspace with no sources.
      loading: isLoading,
    }),
    [items, isLoading],
  );

  return { pool, loading: isLoading };
}

export { POOL_ID as ISSUE_SOURCE_POOL_ID };
